import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useHideScamCheckHistoryMutation, useScamCheckHistory } from '@/hooks/useScamCheck'
import { BrandedSpinner } from '@/components/BrandedSpinner'
import type { ScamCheckVerdict } from '@/types/scamCheck'
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiShield, FiAlertTriangle, FiXCircle, FiLink, FiMessageCircle, FiCamera, FiSearch, FiTrash2 } from 'react-icons/fi'

export const Route = createFileRoute('/_auth/tools/scam-check/history')({
  component: ScamCheckHistoryPage,
})

const verdictConfig: Record<ScamCheckVerdict, { label: string; accentBorder: string; accentText: string; Icon: typeof FiShield }> = {
  SAFE: { label: 'Safe', accentBorder: 'border-l-emerald-500', accentText: 'text-emerald-700', Icon: FiShield },
  CAUTION: { label: 'Caution', accentBorder: 'border-l-amber-500', accentText: 'text-amber-700', Icon: FiAlertTriangle },
  DANGEROUS: { label: 'Dangerous', accentBorder: 'border-l-red-500', accentText: 'text-red-700', Icon: FiXCircle },
}

const inputTypeIcons: Record<string, typeof FiLink> = {
  URL: FiLink,
  TEXT: FiMessageCircle,
  EMAIL: FiMessageCircle, // Legacy: old records may still have EMAIL
  IMAGE: FiCamera,
}

function ScamCheckHistoryPage() {
  const [page, setPage] = useState(1)
  const [query, setQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const { data, isLoading } = useScamCheckHistory(page, query)
  const hideMutation = useHideScamCheckHistoryMutation()
  const pageItemIds = useMemo(() => data?.items?.map((item) => item.id) || [], [data?.items])
  const allPageSelected = pageItemIds.length > 0 && pageItemIds.every((id) => selectedIds.includes(id))

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      if (allPageSelected) {
        return prev.filter((id) => !pageItemIds.includes(id))
      }
      const merged = new Set([...prev, ...pageItemIds])
      return Array.from(merged)
    })
  }

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return
    const confirmed = window.confirm(
      `Delete ${selectedIds.length} check${selectedIds.length === 1 ? '' : 's'} from your history? You can still run new checks anytime.`
    )
    if (!confirmed) return
    await hideMutation.mutateAsync(selectedIds)
    setSelectedIds([])
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-white">
      <div className="mx-auto w-full max-w-6xl px-6 py-8 sm:px-8 sm:py-12">
        {/* Back link */}
        <Link
          to="/tools"
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="mb-7 inline-flex items-center gap-2 text-base text-slate-500 transition-colors hover:text-slate-700"
        >
          <FiArrowLeft className="h-4 w-4" />
          Back to Tools
        </Link>

        <h1 className="mb-2 text-3xl font-medium text-slate-900 sm:text-4xl">Scam Check History</h1>
        <p className="mb-10 text-base text-slate-500 sm:text-lg">Your past scam checks and their results.</p>

        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <label className="relative block w-full sm:max-w-xl">
            <FiSearch className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => {
                setPage(1)
                setQuery(e.target.value)
              }}
              placeholder="Search by title, URL, or content..."
              className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-11 pr-4 text-lg text-slate-700 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          </label>
          <div className="flex items-center gap-4">
            <label className="inline-flex cursor-pointer items-center gap-3 text-lg text-slate-600">
              <input
                type="checkbox"
                checked={allPageSelected}
                onChange={toggleSelectAllOnPage}
                className="relative h-6 w-6 cursor-pointer appearance-none rounded-lg border border-slate-300 bg-white transition-colors after:absolute after:left-[8px] after:top-[3px] after:h-3 after:w-1.5 after:rotate-45 after:border-b-2 after:border-r-2 after:border-white after:opacity-0 after:content-[''] checked:border-violet-700 checked:bg-violet-700 checked:after:opacity-100 focus:outline-none focus:ring-2 focus:ring-violet-200"
              />
              Select all on page
            </label>
            <button
              type="button"
              onClick={handleDeleteSelected}
              disabled={!selectedIds.length || hideMutation.isPending}
              className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-violet-700 bg-violet-700 px-5 py-3 text-lg font-medium text-white transition-colors hover:border-violet-800 hover:bg-violet-800 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:opacity-100"
            >
              <FiTrash2 className="h-5 w-5" />
              {hideMutation.isPending ? 'Deleting...' : `Delete selected${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <BrandedSpinner size="lg" />
          </div>
        ) : !data?.items?.length ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50/40 px-6 py-14 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
              <FiShield className="h-8 w-8 text-slate-300" />
            </div>
            <h2 className="mb-1 text-xl font-medium text-slate-700">No checks yet</h2>
            <p className="mb-6 text-base text-slate-400">
              Use the Scam Checker to analyze suspicious links, messages, or emails.
            </p>
            <Link
              to="/tools"
              search={{ invitationcode: undefined, __clerk_ticket: undefined }}
              className="inline-flex items-center gap-2 text-base font-medium text-slate-700 underline underline-offset-2 transition-colors hover:text-slate-900"
            >
              Go to Scam Checker
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {data.items.map((item) => {
                const config = verdictConfig[item.verdict]
                const InputIcon = inputTypeIcons[item.inputType] || FiLink

                return (
                  <div
                    key={item.id}
                    className={`group flex items-center gap-3 rounded-2xl border border-slate-200 border-l-4 bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-md sm:p-5 ${config.accentBorder}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleSelected(item.id)}
                      className="relative h-6 w-6 shrink-0 cursor-pointer appearance-none rounded-lg border border-slate-300 bg-white transition-colors after:absolute after:left-[8px] after:top-[3px] after:h-3 after:w-1.5 after:rotate-45 after:border-b-2 after:border-r-2 after:border-white after:opacity-0 after:content-[''] checked:border-violet-700 checked:bg-violet-700 checked:after:opacity-100 focus:outline-none focus:ring-2 focus:ring-violet-200"
                      aria-label={`Select history item ${item.headline}`}
                    />
                    <Link
                      to="/tools/scam-check/$checkId"
                      params={{ checkId: item.id }}
                      search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                      className="flex min-w-0 flex-1 items-center gap-5 rounded-xl px-1 py-1"
                    >
                      {/* Verdict badge */}
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-slate-50">
                        <config.Icon className={`h-5 w-5 ${config.accentText}`} />
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-lg font-medium text-slate-800 transition-colors group-hover:text-slate-900">
                          {item.headline}
                        </div>
                        <div className="mt-2 flex items-center gap-2.5">
                          <InputIcon className="h-4 w-4 text-slate-400" />
                          <span className="truncate text-base text-slate-500">
                            {item.inputType === 'IMAGE' ? 'Image upload' : item.inputType === 'VIDEO' ? 'Video upload' : (item.domain || item.inputValue)}
                          </span>
                          <span className="text-base text-slate-300">
                            {new Date(item.createdAt).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </span>
                        </div>
                      </div>

                      {/* Verdict label */}
                      <span className={`flex-shrink-0 rounded-full border px-3 py-1 text-sm font-medium ${config.accentText} ${config.accentBorder} bg-white`}>
                        {config.label}
                      </span>
                    </Link>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {data.pages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-5">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-lg px-4 py-2.5 text-base text-slate-600 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <FiChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <span className="text-base text-slate-500">
                  Page {page} of {data.pages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                  disabled={page === data.pages}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-lg px-4 py-2.5 text-base text-slate-600 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  Next
                  <FiChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
